import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

// @ld
// @custom
import * as urls from 'constants/urls';
import Segment from 'components/segment';
import PageNotFound from 'components/layouts/page-not-found';
import AppProvider from 'components/app-provider';
import AppIdleTimer from 'components/app-idle-timer';
import ErrorFallback from 'components/error-fallback';
import DelightedSurvey from 'components/delighted-survey';
import AppLayoutRoute from 'components/layouts/app-layout-route';
import AuthLayoutRoute from 'components/layouts/auth-layout-route';
import AuthActionLayoutRoute from 'components/layouts/auth-action-layout-route';

import { PageLoader, GlobalStyles, ThemeProvider, StagingEnvBanner } from '@ld/core';

const App = () => {
  return (
    <ThemeProvider>
      <GlobalStyles />
      <Router>
        <AppProvider>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <React.Suspense fallback={<PageLoader />}>
              <Switch>
                <AuthLayoutRoute
                  exact
                  path='/'
                  component={React.lazy(
                    () => import(/* webpackChunkName: 'Signin' */ './pages/auth/signin')
                  )}
                />
                <AuthActionLayoutRoute
                  exact
                  path={urls.twoFactorAuthenticationUrl}
                  component={React.lazy(
                    () =>
                      import(/* webpackChunkName: 'Signin' */ './pages/auth/two-factor')
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.forgotPasswordUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'ForgotPassword' */ './pages/auth/forgot-password'
                      )
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.createAccountUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'CreateAnAccount' */ './pages/auth/create-an-account'
                      )
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.createAccountSuccessUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'CreateAccountSuccess' */ './pages/auth/create-account-success'
                      )
                  )}
                />
                <AuthActionLayoutRoute
                  exact
                  path={urls.verifyEmailUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'VerifyEmail' */ './pages/auth/verify-email'
                      )
                  )}
                />
                <AuthActionLayoutRoute
                  exact
                  path={urls.authActionUrl}
                  component={React.lazy(
                    () =>
                      import(/* webpackChunkName: 'AuthAction' */ './pages/auth/action')
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.lockAccountUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'LockAccount' */ './pages/auth/lock-account'
                      )
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.lockedAccountUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'LockedAccount' */ './pages/auth/locked-account'
                      )
                  )}
                />
                <AuthLayoutRoute
                  exact
                  path={urls.unauthorizedUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'Unauthorized' */ './pages/auth/unauthorized'
                      )
                  )}
                />
                <Route
                  path={urls.profilesUrl}
                  component={React.lazy(
                    () => import(/* webpackChunkName: 'Profiles' */ './pages/profiles')
                  )}
                />
                <AppLayoutRoute
                  path={urls.advisorsUrl}
                  component={React.lazy(
                    () => import(/* webpackChunkName: 'Advisors' */ './pages/advisors')
                  )}
                />
                <AppLayoutRoute
                  path={urls.organisationUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'Organisation' */ './pages/organisation'
                      )
                  )}
                />
                <AppLayoutRoute
                  path={urls.myAccountUrl}
                  component={React.lazy(
                    () => import(/* webpackChunkName: 'Account' */ './pages/account')
                  )}
                />
                <AppLayoutRoute
                  path={urls.helpAndSupportUrl}
                  component={React.lazy(
                    () =>
                      import(
                        /* webpackChunkName: 'HelpAndFeedback' */ './pages/help-and-feedback'
                      )
                  )}
                />
                <Route path='*' component={PageNotFound} />
              </Switch>
            </React.Suspense>
          </ErrorBoundary>
          <Segment />
          <AppIdleTimer />
          <DelightedSurvey />
          <StagingEnvBanner />
        </AppProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
